// What the step controls show for a review state, without React: which
// buttons are enabled, their labels, whether the reader is out on a line of
// their own, the contextual exit at the bar's right, and the status line. The
// board's Controls constraint switches directions off, or, with a range (a
// puzzle), makes the steps look along the path within it (`ranged-steps.ts`).
// The panel in `index.tsx` draws this through `StepControls.tsx`.
import { isExploring, moveLabel, type ReviewState } from '@chess/api-review';
import type { ControlsConstraint } from '@chess/api-constraints';
import { rangedSteps } from './ranged-steps.ts';
import { outerSteps, type OuterSteps } from './outer-steps.ts';

/**
 * What a step or the exit does: a review method, or, for the outer buttons
 * in the game, `outerBack`/`outerForward`: the previous or next mistake,
 * the start or the end when there is none that way (`outer`, outer-steps.ts;
 * the panel resolves them to `go`).
 */
export type StepAction = 'toStart' | 'stepBackward' | 'stepForward' | 'toEnd' | 'returnToGame' | 'outerBack' | 'outerForward';

/**
 * The contextual exit in the bar's right zone: the one way out of where the
 * reader is. On a line of their own it is back to the game (`returnToGame`,
 * Escape); in a puzzle while looking back, back to the puzzle (`toEnd`, the
 * range's end). Absent otherwise; the zone keeps its width so the centre
 * never shifts.
 */
export interface StepExit {
  kind: 'game' | 'puzzle';
  action: StepAction;
  label: string;
}

export interface StepControlsView {
  /** A game is open. */
  ready: boolean;
  /** A Controls range holds the steps (a puzzle): they look rather than move. */
  ranged: boolean;
  /**
   * An owner holds the controls (a puzzle: a range, or a direction switched
   * off): only what it allows is drawn in the centre, look back and back to
   * the owner's position; start, next and end are left out rather than greyed.
   */
  held: boolean;
  /** The reader is out on a line of their own, outside a range: forward is off, the exit is back to the game. */
  deviated: boolean;
  /** Start and previous. */
  canBack: boolean;
  /** Next and end. */
  canForward: boolean;
  labels: { start: string; back: string; forward: string; end: string };
  /**
   * In the game (not held, not on a line): where the outer buttons go, the
   * previous and the next mistake, or the start and the end when none lies
   * that way; they then say so in their labels and wear the mistake's glyph.
   */
  outer?: OuterSteps;
  /**
   * The bar's three zones. The centre holds the steps (`held` says which);
   * the right holds the exit, or nothing; the left is empty and as wide as
   * the right, so the centre is centred whatever the right shows.
   */
  zones: { left: null; centre: 'steps' | 'held'; right: StepExit | null };
  /** Where the reader is, for assistive tech. */
  status: string;
}

/** Where the reader is: the status line read out as they step. */
export function statusText(state: ReviewState): string {
  switch (state.status) {
    case 'idle':
      return 'No game open.';
    case 'loading':
      return 'Loading the game…';
    case 'missing':
      return 'That game is not in the library.';
    case 'failed':
      return state.error ? `Could not open the game: ${state.error}` : 'Could not open the game.';
    case 'ready': {
      const game = state.game!;
      const total = game.moves.length;
      if (state.exploring) {
        const from = state.exploring.fromPly === 0 ? 'the start' : moveLabel(game, state.exploring.fromPly);
        const n = state.exploring.moves.length;
        return `Exploring from ${from} · ${n} ${n === 1 ? 'move' : 'moves'}`;
      }
      if (state.ply === 0) return `Starting position · ${total} plies`;
      const where = state.ply === total ? 'Final position' : moveLabel(game, state.ply);
      return `${where} · ${state.ply} of ${total}`;
    }
  }
}

/** The step controls for a state under the board's Controls constraint and its owner. */
export function stepControlsView(
  state: ReviewState,
  controls: Pick<ControlsConstraint, 'back' | 'forward' | 'range'>,
  owner?: { label: string },
): StepControlsView {
  const ranged = !!controls.range;
  const held = ranged || !controls.back || !controls.forward;
  const ready = state.status === 'ready' && !!state.game;
  const deviated = ready && isExploring(state) && !ranged;
  const total = state.game?.moves.length ?? 0;
  const looking = rangedSteps(state, controls.range);
  const canBack = ready && controls.back && (ranged ? looking.canBack : deviated || state.ply > 0);
  const canForward = ready && controls.forward && (ranged ? looking.canForward : !deviated && state.ply < total);
  const backToOwner = `Back to the ${(owner?.label ?? 'board').toLowerCase()}`;
  const outer = ready && !held && !deviated ? outerSteps(state) : undefined;
  // The exit: out of the reader's line, or, while looking back within a range, to its end.
  const exit: StepExit | null = deviated
    ? { kind: 'game', action: 'returnToGame', label: 'Back to the game' }
    : ranged && canForward
      ? { kind: 'puzzle', action: 'toEnd', label: backToOwner }
      : null;
  return {
    ready,
    ranged,
    held,
    deviated,
    canBack,
    canForward,
    outer,
    labels: {
      start: deviated ? 'Leave your line and go to the starting position' : (outer?.back.label ?? 'Go to the starting position'),
      back: deviated ? 'Take back the last move of your line' : held ? 'Look back' : 'Previous move',
      forward: 'Next move',
      end: held ? backToOwner : (outer?.forward.label ?? 'Go to the final position'),
    },
    zones: { left: null, centre: held ? 'held' : 'steps', right: exit },
    status: ranged ? `${owner?.label ?? 'Constrained'} · step back to review, the moves ahead are hidden` : statusText(state),
  };
}
